package com.evoapps.evoshift.ui.reports

import com.evoapps.evoshift.widgets.EvoRadioGroup
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.DialogAction
import androidx.compose.material.icons.filled.Print
import androidx.compose.material.icons.Icons
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.ReportPrintViewModel
import com.evoapps.evoshift.viewmodels.WorkingTimeScope
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.print_action
import evoshift.composeapp.generated.resources.print_choose_employee
import evoshift.composeapp.generated.resources.print_payroll
import evoshift.composeapp.generated.resources.print_payroll_all
import evoshift.composeapp.generated.resources.print_payroll_one
import evoshift.composeapp.generated.resources.print_scope_all
import evoshift.composeapp.generated.resources.print_scope_employee
import evoshift.composeapp.generated.resources.print_scope_workplace
import evoshift.composeapp.generated.resources.print_working_time
import org.jetbrains.compose.resources.stringResource
import org.kodein.di.compose.localDI
import org.kodein.di.direct
import org.kodein.di.instance

/** A person to choose in the print dialogs. */
data class PrintPerson(val id: String, val name: String)

@Composable
private fun rememberPrintViewModel(): ReportPrintViewModel {
    val di = localDI()
    return remember { di.direct.instance<ReportPrintViewModel>() }
}

/** Working-time record of a month: all my workplaces, this workplace, or one person. */
@Composable
fun PrintWorkingTimeDialog(
    workplaceId: String,
    year: Int,
    month: Int,
    people: List<PrintPerson>,
    onDismiss: () -> Unit
) {
    val viewModel = rememberPrintViewModel()
    var scope by remember { mutableStateOf(WorkingTimeScope.WORKPLACE) }
    var person by remember { mutableStateOf(people.firstOrNull()) }
    PrintDialog(
        title = stringResource(Res.string.print_working_time),
        viewModel = viewModel,
        canPrint = scope != WorkingTimeScope.EMPLOYEE || person != null,
        onPrint = { viewModel.printWorkingTime(scope, workplaceId, year, month, person?.id) },
        onDismiss = onDismiss
    ) {
        Choice(stringResource(Res.string.print_scope_workplace), scope == WorkingTimeScope.WORKPLACE) { scope = WorkingTimeScope.WORKPLACE }
        Choice(stringResource(Res.string.print_scope_all), scope == WorkingTimeScope.ALL_MY_WORKPLACES) { scope = WorkingTimeScope.ALL_MY_WORKPLACES }
        Choice(stringResource(Res.string.print_scope_employee), scope == WorkingTimeScope.EMPLOYEE) { scope = WorkingTimeScope.EMPLOYEE }
        if (scope == WorkingTimeScope.EMPLOYEE) PersonPicker(people, person) { person = it }
    }
}

/** The month's payroll: everyone (summary and payslips) or one payslip. */
@Composable
fun PrintPayrollDialog(rosterId: String, people: List<PrintPerson>, onDismiss: () -> Unit) {
    val viewModel = rememberPrintViewModel()
    var everyone by remember { mutableStateOf(true) }
    var person by remember { mutableStateOf(people.firstOrNull()) }
    PrintDialog(
        title = stringResource(Res.string.print_payroll),
        viewModel = viewModel,
        canPrint = everyone || person != null,
        onPrint = { viewModel.printPayroll(rosterId, person?.id.takeIf { !everyone }) },
        onDismiss = onDismiss
    ) {
        Choice(stringResource(Res.string.print_payroll_all), everyone) { everyone = true }
        Choice(stringResource(Res.string.print_payroll_one), !everyone) { everyone = false }
        if (!everyone) PersonPicker(people, person) { person = it }
    }
}

@Composable
private fun PrintDialog(
    title: String,
    viewModel: ReportPrintViewModel,
    canPrint: Boolean,
    onPrint: () -> Unit,
    onDismiss: () -> Unit,
    options: @Composable () -> Unit
) {
    val state by viewModel.state.collectAsState()
    var requested by remember { mutableStateOf(false) }
    // Close once the print dialog is up; stay open to show why nothing was printed.
    LaunchedEffect(state, requested) {
        if (requested && !state.busy) {
            if (state.error == null) onDismiss() else requested = false
        }
    }
    EvoDialog(
        title = title,
        icon = Icons.Default.Print,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        dismissible = !state.busy,
        confirm = DialogAction(
            stringResource(Res.string.print_action),
            { requested = true; onPrint() },
            enabled = canPrint,
            loading = state.busy,
            icon = Icons.Default.Print
        ),
        bodyArrangement = Arrangement.spacedBy(4.dp)
    ) {
        options()
        state.error?.let {
            Text(it.asString(), color = ErrorOnNavy, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(top = 8.dp))
        }
    }
}

@Composable
private fun Choice(label: String, selected: Boolean, onClick: () -> Unit) {
    EvoRadioGroup(
        options = listOf(label),
        selected = label.takeIf { selected },
        onSelect = { onClick() },
        optionLabel = { it }
    )
}

@Composable
private fun PersonPicker(people: List<PrintPerson>, selected: PrintPerson?, onSelected: (PrintPerson) -> Unit) {
    EvoDropdown(
        label = stringResource(Res.string.print_choose_employee),
        options = people,
        selected = selected,
        onSelected = onSelected,
        optionLabel = { it.name },
        modifier = Modifier.padding(start = 38.dp, top = 4.dp, bottom = 4.dp)
    )
}
